/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/catalyst/input';
import { useUpdateAgentChatWorkspaceMutation } from '@/services/api/agentChatWorkspaceService';
import { AgentChatWorkspace } from '@/services/types/agent-chat-workspace-types';

interface EditWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  workspace: AgentChatWorkspace;
}

export const EditWorkspaceDialog = ({
  isOpen,
  onClose,
  matterId,
  workspace,
}: EditWorkspaceDialogProps) => {
  const [updateWorkspace, { isLoading }] =
    useUpdateAgentChatWorkspaceMutation();

  const [name, setName] = useState('');

  // Set form values when workspace changes
  useEffect(() => {
    if (workspace) {
      setName(workspace.name || '');
    }
  }, [workspace]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateWorkspace({
        matterId,
        workspaceId: workspace.id,
        data: {
          name,
          disable_exhibit_pages: workspace.disable_exhibit_pages,
          exhibit_label_format: workspace.exhibit_label_format,
        },
      }).unwrap();

      onClose();
    } catch (error) {
      toast.error('Failed to update workspace');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="max-w-md mx-auto">
      <div className="p-6">
        <DialogTitle>Edit Workspace</DialogTitle>

        <DialogBody>
          <form onSubmit={handleSubmit} id="edit-workspace-form">
            <div className="space-y-4">
              <div className="form-group">
                <label htmlFor="edit-name" className="block mb-2 font-medium">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Enter workspace title"
                  className="w-full"
                />
              </div>
            </div>
          </form>
        </DialogBody>

        <DialogActions>
          <Button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-workspace-form"
            disabled={!name || isLoading}
            className={`bg-blue-500 text-white ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-600'}`}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};
